import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, X, ChevronDown, Phone, FileText, Shield } from 'lucide-react';
import { COMPANY_INFO, PRODUCTS, PRODUCT_CATEGORIES } from '../data';
import './Navbar.css';

const PRODUCT_GROUPS = PRODUCT_CATEGORIES.map(cat => ({
  label: cat.label,
  items: PRODUCTS.filter(p => p.category === cat.id).map(p => ({
    label: p.shortTitle,
    path: `/products/${p.slug}`,
  })),
}));

const NAV_ITEMS = [
  { label: 'Home', path: '/' },
  {
    label: 'About',
    path: '/about',
    children: [
      { label: 'Who We Are', path: '/about#who-we-are' },
      { label: 'Our Team', path: '/about#team' },
      { label: 'Why Choose Us', path: '/about#why' },
    ],
  },
  {
    label: 'Products',
    path: '/products',
    groups: PRODUCT_GROUPS,
  },
  { label: 'Financing', path: '/financing' },
  { label: 'Careers', path: '/careers' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar({ onSearchOpen }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleMouseEnter = (label) => {
    clearTimeout(timeoutRef.current);
    setActiveDropdown(label);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const hasMenu = item => item.children || item.groups;

  return (
    <>
      {/* TOP BAR */}
      <div className="navbar-topbar">
        <div className="container">
          <div className="topbar-inner">
            <div className="topbar-left">
              <span className="topbar-info">
                <Phone size={12} />
                <a href={`tel:${COMPANY_INFO.phone}`}>{COMPANY_INFO.phone}</a>
              </span>
              <span className="topbar-info">
                {COMPANY_INFO.email}
              </span>
            </div>
            <Link to="/certificate" className="topbar-cert-link">
              <FileText size={12} /> Download Your COI
            </Link>
          </div>
        </div>
      </div>

      {/* MAIN NAVBAR */}
      <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
        <div className="container">
          <div className="navbar-inner">
            {/* Logo */}
            <Link to="/" className="navbar-logo">
              <img
                src={COMPANY_INFO.logo}
                alt="Prime Sentinel Insurance"
                onError={e => { e.target.style.display='none'; }}
              />
              <div className="navbar-logo-text">
                <span className="navbar-logo-name">Prime Sentinel</span>
                <span className="navbar-logo-sub">Insurance Solutions</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <ul className="navbar-links">
              {NAV_ITEMS.map(item => (
                <li
                  key={item.label}
                  className="nav-item"
                  onMouseEnter={() => hasMenu(item) && handleMouseEnter(item.label)}
                  onMouseLeave={hasMenu(item) ? handleMouseLeave : undefined}
                >
                  {hasMenu(item) ? (
                    <>
                      <Link
                        to={item.path}
                        className={`nav-link${isActive(item.path) ? ' active' : ''}${activeDropdown === item.label ? ' open' : ''}`}
                      >
                        {item.label}
                        <ChevronDown size={14} />
                      </Link>
                      {activeDropdown === item.label && (
                        <div
                          className={`nav-dropdown${item.groups ? ' nav-dropdown-wide' : ''}`}
                          onMouseEnter={() => clearTimeout(timeoutRef.current)}
                          onMouseLeave={handleMouseLeave}
                        >
                          {item.children && item.children.map(child => (
                            <Link key={child.path} to={child.path}>{child.label}</Link>
                          ))}
                          {item.groups && (
                            <div className="nav-dropdown-groups">
                              {item.groups.map(group => (
                                <div key={group.label} className="nav-dropdown-group">
                                  <div className="nav-dropdown-group-label">{group.label}</div>
                                  {group.items.map(child => (
                                    <Link key={child.path} to={child.path}>{child.label}</Link>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`nav-link${isActive(item.path) ? ' active' : ''}`}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Actions */}
            <div className="navbar-actions">
              <button className="nav-search-btn" onClick={onSearchOpen} aria-label="Search">
                <Search size={17} />
              </button>
              <Link to="/get-quote" className="nav-cta">
                <Shield size={14} /> Get a Quote
              </Link>
              <button
                className={`hamburger-btn${mobileOpen ? ' open' : ''}`}
                onClick={() => setMobileOpen(o => !o)}
                aria-label="Toggle menu"
              >
                <span className="hamburger-line" />
                <span className="hamburger-line" />
                <span className="hamburger-line" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* MOBILE DRAWER */}
      <div className={`mobile-overlay${mobileOpen ? ' open' : ''}`} onClick={() => setMobileOpen(false)} />
      <div className={`mobile-drawer${mobileOpen ? ' open' : ''}`}>
        <div className="mobile-drawer-header">
          <Link to="/" className="navbar-logo" onClick={() => setMobileOpen(false)}>
            <div className="navbar-logo-text">
              <span className="navbar-logo-name">Prime Sentinel</span>
              <span className="navbar-logo-sub">Insurance Solutions</span>
            </div>
          </Link>
          <button className="mobile-close" onClick={() => setMobileOpen(false)}>
            <X size={18} />
          </button>
        </div>
        <nav className="mobile-nav">
          {NAV_ITEMS.map(item => (
            <div key={item.label} className="mobile-nav-item">
              {hasMenu(item) ? (
                <>
                  <button
                    className={`mobile-nav-link${mobileExpanded === item.label ? ' expanded' : ''}${isActive(item.path) ? ' active' : ''}`}
                    onClick={() => setMobileExpanded(e => e === item.label ? null : item.label)}
                  >
                    {item.label}
                    <ChevronDown size={16} />
                  </button>
                  {mobileExpanded === item.label && (
                    <div className="mobile-sub-links">
                      {item.children && item.children.map(child => (
                        <Link key={child.path} to={child.path} onClick={() => setMobileOpen(false)}>
                          {child.label}
                        </Link>
                      ))}
                      {item.groups && item.groups.map(group => (
                        <div key={group.label}>
                          <div className="mobile-sub-group-label">{group.label}</div>
                          {group.items.map(child => (
                            <Link key={child.path} to={child.path} onClick={() => setMobileOpen(false)}>
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  className={`mobile-nav-link${isActive(item.path) ? ' active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
        </nav>
        <div className="mobile-nav-footer">
          <Link to="/get-quote" className="btn-primary" onClick={() => setMobileOpen(false)}>
            <Shield size={15} /> Get a Free Quote
          </Link>
          <button className="btn-outline" onClick={() => { setMobileOpen(false); onSearchOpen(); }}>
            <Search size={15} /> Search
          </button>
        </div>
      </div>
    </>
  );
}